import { HttpMethod, HttpModel } from '@/Shared/domain'
import { PaginationModel } from '@/Shared/domain/models'
import { axiosFetch, createQueryString } from '@/Shared/infrastructure'
import {
  addNewTpv,
  getTpvsData,
  getTpvsDataExcel,
  getTerminalsMassiveExcelTemplate,
  getTerminalsMassiveExcelTemplateEmpty,
  addTerminalsMassive,
  addTerminalsMassiveValidation,
} from '@/Shared/infrastructure/endpoints'
import {
  TpvModel,
  TpvsRepository,
  TpvExcelModel,
  TpvExcelTemplateResponse,
  TpvExcelUpload,
  TpvExcelUploadErrorsResponse,
  ExcelUploadError,
  TpvExcelUploadResponse
} from '@/Store/domain'
import { TpvFilters, TpvForm } from '@/Store/ui/hooks'

export function createApiTpvRepository(): TpvsRepository {
  return new ApiTpvRepository()
}

const ROWS_PER_PAGE = 8

type ApiTpvModel = {
  fullAddress: string
  hasMerchantHardDependency: boolean
  masterGroupAlias: string | null
  masterGroupId: number | null
  merchantId: number
  merchantName: string
  merchantParentId: number | null
  merchantParentName: string | null
  superMasterGroupAlias: string | null
  terminalCompleteMid: string
  terminalCreationDate: string
  terminalId: 1585747
  terminalMid: string
  terminalName: string
  terminalStatusCode: number
  terminalStatusName: string
  terminalSubMid: string | null
  terminalZipCode: string
  isPedingActivation: boolean
}

type ApiUploadExcelRequest = {
  excel: string,
}

type ApiUploadExcelResponse = HttpModel<{
  fileResponse: string | null | undefined
  rowErrors: ExcelUploadError[]
  errorCount: number
} | null>


const convertToDomain = (
  response: ApiUploadExcelResponse
): TpvExcelUploadErrorsResponse => ({
  fileResponse: response.data?.fileResponse,
  rowErrors: response.data?.rowErrors ?? [],
  errorCount: response.data?.errorCount ?? 0,
})
export class ApiTpvRepository implements TpvsRepository {
  async GetTpvsData(
    merchantTargetId: number,
    pageNum: number,
    filters?: TpvFilters,
  ): Promise<HttpModel<PaginationModel<TpvModel>>> {
    const response = await axiosFetch(
      getTpvsData.concat(
        createQueryString({
          merchantTargetId: merchantTargetId.toString(),
          pageNum: pageNum.toString(),
        }),
      ),
      HttpMethod.POST,
      {
        ...(filters?.multiFieldFileter ? { multiFieldFileter: filters.multiFieldFileter } : {}),
        ...(filters?.merchantTargetList?.length ? { merchantTargetList: filters.merchantTargetList } : {}),
        paginationModel: {
          numberOfRecordsPerPage: ROWS_PER_PAGE,
          pageNumber: pageNum - 1,
          orderByField: '',
          sortDescending: true,
        },
      },
    )

    const isArrayResponse = Array.isArray(response.data)
    const rows: ApiTpvModel[] = isArrayResponse
      ? response.data
      : (response.data?.rows ?? [])

    if (!rows.length) {
      return {
        data: { rows: [], totalRows: 0 },
        meta: response.meta,
      }
    }

    return {
      data: {
        rows: rows.map((item: ApiTpvModel) => ({
          terminalMid: item.terminalMid,
          affiliateCode: item.merchantId?.toString(),
          local: item.merchantName ?? item.terminalName,
          fullAddress: item.fullAddress,
          commerceCode: item.terminalCompleteMid,
          isPedingActivation: item.isPedingActivation,
        })),
        totalRows: isArrayResponse ? rows.length : (response.data?.totalRows ?? rows.length),
      },
      meta: response.meta,
    }
  }

  async AddNewTpv(data: TpvForm): Promise<HttpModel<boolean>> {
    const response = await axiosFetch(addNewTpv, HttpMethod.POST, data)

    if (!response.data) {
      return {
        data: false,
        meta: response.meta,
      }
    }

    return {
      data: response.data,
      meta: response.meta,
    }
  }

  async GetTpvsDataExcel(merchantTargetId: number, filters?: TpvFilters): Promise<HttpModel<TpvExcelModel>> {
    const response = await axiosFetch(
      getTpvsDataExcel.concat(
        createQueryString({
          merchantTargetId: merchantTargetId.toString(),
        }),
      ),
      HttpMethod.POST,
      {
        ...(filters?.multiFieldFileter ? { multiFieldFileter: filters.multiFieldFileter } : {}),
        ...(filters?.merchantTargetList?.length ? { merchantTargetList: filters.merchantTargetList } : {}),
      },
    )

    return {
      data: response.data,
      meta: response.meta,
    }
  }

  async GetTerminalsMassiveExcelTemplate(
    merchantTargetId: number,
    empty: boolean,
  ): Promise<HttpModel<TpvExcelTemplateResponse>> {
    const endpoint = empty
      ? getTerminalsMassiveExcelTemplateEmpty
      : getTerminalsMassiveExcelTemplate

    const response = await axiosFetch(
      endpoint.concat(
        createQueryString({
          merchantTargetId: merchantTargetId.toString(),
        }),
      ),
      HttpMethod.GET,
      {},
    )

    return {
      data: response.data,
      meta: response.meta,
    }
  }
  async validateOrder(
    validateOrder: TpvExcelUpload
  ): Promise<HttpModel<TpvExcelUploadResponse>> {
    const data: ApiUploadExcelRequest = {
      excel: validateOrder.excel,
    }

    const response: ApiUploadExcelResponse = await axiosFetch(
      addTerminalsMassiveValidation,
      HttpMethod.POST,
      data
    )

    return {
      data: response.data ?? undefined,
      meta: response.meta,
    }
  }
  async validateAndInsertTPVsExcel(
    validateOrder: TpvExcelUpload
  ): Promise<HttpModel<TpvExcelUploadResponse>> {
    const data: ApiUploadExcelRequest = {
      excel: validateOrder.excel,
    }

    const response: ApiUploadExcelResponse = await axiosFetch(
      addTerminalsMassive,
      HttpMethod.POST,
      data
    )

    return {
      data: response.data ?? undefined,
      meta: response.meta,
    }
  }
}
